import { getAccessToken } from "../utils/auth";
import { getUserRole } from "../utils/sessionStore";
import React, { useCallback, useEffect, useState } from "react";
import axios from "../utils/api.js";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { useNavigate } from "react-router-dom";

function formatHours(seconds) {
  if (seconds == null || Number.isNaN(Number(seconds))) return "—";
  const h = Number(seconds) / 3600;
  if (h < 1) return `${Math.round(Number(seconds) / 60)} min`;
  return `${h.toFixed(1)} h`;
}

function formatDate(value) {
  if (!value) return "—";
  const d = new Date(value);
  return Number.isNaN(d.getTime())
    ? "—"
    : d.toLocaleString("en-IN", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
}

function PoolBar({ label, remaining, quota, color }) {
  const safeQuota = quota > 0 ? quota : 1;
  const pct = Math.min(100, Math.round((remaining / safeQuota) * 100));
  return (
    <Box sx={{ mb: 1.5 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
        <Typography variant="caption" sx={{ fontWeight: 600, color: "#475569" }}>
          {label}
        </Typography>
        <Typography variant="caption" sx={{ fontWeight: 700, color: "#0f172a" }}>
          {formatHours(remaining)}
          {quota ? ` / ${formatHours(quota)}` : ""}
        </Typography>
      </Box>
      <Box sx={{ height: 8, borderRadius: 999, bgcolor: "#e2e8f0", overflow: "hidden" }}>
        <Box sx={{ width: `${pct}%`, height: "100%", bgcolor: color, borderRadius: 999 }} />
      </Box>
    </Box>
  );
}

const CAN_GRANT_ADMIN_TIME = new Set(["Hr", "AdminController"]);

export default function AdminTimePoolPage() {
  const navigate = useNavigate();
  const canGrant = CAN_GRANT_ADMIN_TIME.has(getUserRole());
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [presets, setPresets] = useState(["1h", "2h"]);
  const [error, setError] = useState("");
  const [grantOpen, setGrantOpen] = useState(false);
  const [grantTarget, setGrantTarget] = useState(null);
  const [grantForm, setGrantForm] = useState({ preset: "1h", customMinutes: 30, note: "" });
  const [granting, setGranting] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const loadPools = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await axios.get("/admin/users-time-pools", {
        headers: { Authorization: `Bearer ${getAccessToken()}` },
      });
      if (res.data?.success) {
        setRows(Array.isArray(res.data.data) ? res.data.data : []);
        setSummary(res.data.summary || null);
        if (Array.isArray(res.data.presets) && res.data.presets.length) {
          setPresets(res.data.presets);
        }
      } else {
        setRows([]);
        setError(res.data?.message || "Could not load time pools.");
      }
    } catch (err) {
      setRows([]);
      setError(err.response?.data?.message || "Could not load time pools.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPools();
  }, [loadPools]);

  const openGrant = (row) => {
    setGrantTarget(row);
    setGrantForm({ preset: "1h", customMinutes: 30, note: "" });
    setGrantOpen(true);
  };

  const submitGrant = async () => {
    if (!grantTarget?.userId || granting) return;
    setGranting(true);
    try {
      const body = {
        preset: grantForm.preset,
        note: grantForm.note?.trim() || undefined,
      };
      if (grantForm.preset === "custom") {
        body.customMinutes = Number(grantForm.customMinutes);
      }
      const res = await axios.post(
        `/admin/users/${grantTarget.userId}/admin-time`,
        body,
        { headers: { Authorization: `Bearer ${getAccessToken()}` } }
      );
      setSnackbar({
        open: true,
        severity: "success",
        message:
          res.data?.message ||
          `Admin bonus time granted to ${grantTarget.email}.`,
      });
      setGrantOpen(false);
      loadPools();
    } catch (err) {
      setSnackbar({
        open: true,
        severity: "error",
        message: err.response?.data?.message || "Could not grant admin time.",
      });
    } finally {
      setGranting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "60vh" }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 4, bgcolor: "#f8f9fa", minHeight: "100vh" }}>
      <Paper elevation={0} sx={{ p: 4, borderRadius: 4, maxWidth: 1400, mx: "auto" }}>
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, mb: 3, flexWrap: "wrap" }}>
          <IconButton onClick={() => navigate("/subscription-analysis")}>
            <ArrowBackIcon />
          </IconButton>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h4" sx={{ fontWeight: 700 }}>
              Admin Time Pools
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Separate bonus hours persist across monthly main-plan resets. Consumed after main and
              add-on time is used.
            </Typography>
          </Box>
        </Box>

        {error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : null}

        {summary ? (
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ borderRadius: 3, bgcolor: "#eff6ff" }}>
                <CardContent>
                  <Typography variant="body2" color="text.secondary">
                    Active subscribers
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 800, color: "#1d4ed8" }}>
                    {summary.users}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ borderRadius: 3, bgcolor: "#ecfdf5" }}>
                <CardContent>
                  <Typography variant="body2" color="text.secondary">
                    Main pool remaining
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 800, color: "#047857" }}>
                    {formatHours(summary.mainRemainingSeconds)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ borderRadius: 3, bgcolor: "#f5f3ff" }}>
                <CardContent>
                  <Typography variant="body2" color="text.secondary">
                    Add-on pool remaining
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 800, color: "#6d28d9" }}>
                    {formatHours(summary.addonRemainingSeconds)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ borderRadius: 3, bgcolor: "#fff7ed" }}>
                <CardContent>
                  <Typography variant="body2" color="text.secondary">
                    Admin bonus remaining
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 800, color: "#c2410c" }}>
                    {formatHours(summary.adminRemainingSeconds)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {summary.usersWithAdminPool} user(s) with bonus time
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        ) : null}

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>User</TableCell>
                <TableCell>Plan</TableCell>
                <TableCell>Time pools</TableCell>
                <TableCell>Total remaining</TableCell>
                <TableCell>Recent grants</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4, color: "text.secondary" }}>
                    No active subscription users with time pools.
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row) => (
                  <TableRow key={row.userId} hover>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {row.name || "—"}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {row.email}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{row.planName || "—"}</Typography>
                      <Chip label={row.planStatus || "—"} size="small" sx={{ mt: 0.5, height: 22 }} />
                    </TableCell>
                    <TableCell sx={{ minWidth: 220 }}>
                      <PoolBar
                        label="Main"
                        remaining={row.pools.main.remainingSeconds}
                        quota={row.pools.main.quotaSeconds}
                        color="#2563eb"
                      />
                      <PoolBar
                        label="Add-on"
                        remaining={row.pools.addon.remainingSeconds}
                        quota={row.pools.addon.purchasedSeconds}
                        color="#7c3aed"
                      />
                      <PoolBar
                        label="Admin bonus"
                        remaining={row.pools.admin.remainingSeconds}
                        quota={row.pools.admin.grantedSeconds}
                        color="#ea580c"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        {formatHours(row.pools.totalRemainingSeconds)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        used {formatHours(row.pools.totalUsedSeconds)}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ maxWidth: 200 }}>
                      {(row.recentGrants || []).length === 0 ? (
                        <Typography variant="caption" color="text.secondary">
                          No grants yet
                        </Typography>
                      ) : (
                        row.recentGrants
                          .slice()
                          .reverse()
                          .slice(0, 2)
                          .map((g, i) => (
                            <Typography key={i} variant="caption" display="block" color="text.secondary">
                              +{formatHours(g.secondsGranted)} · {formatDate(g.grantedAt)}
                            </Typography>
                          ))
                      )}
                    </TableCell>
                    <TableCell align="right">
                      {canGrant ? (
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<AddCircleOutlineIcon />}
                          onClick={() => openGrant(row)}
                        >
                          Grant time
                        </Button>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          View only
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog open={grantOpen} onClose={() => setGrantOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Grant admin bonus time</DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {grantTarget?.email} — bonus time is stored in a separate pool and does not reset when
            the monthly main allowance renews.
          </Typography>
          <FormControl fullWidth size="small">
            <InputLabel>Preset</InputLabel>
            <Select
              label="Preset"
              value={grantForm.preset}
              onChange={(e) => setGrantForm({ ...grantForm, preset: e.target.value })}
            >
              {presets.map((p) => (
                <MenuItem key={p} value={p}>
                  {p === "custom" ? "Custom duration" : p}
                </MenuItem>
              ))}
              {!presets.includes("custom") ? (
                <MenuItem value="custom">Custom duration</MenuItem>
              ) : null}
            </Select>
          </FormControl>
          {grantForm.preset === "custom" ? (
            <TextField
              label="Custom minutes"
              type="number"
              size="small"
              value={grantForm.customMinutes}
              onChange={(e) =>
                setGrantForm({ ...grantForm, customMinutes: e.target.value })
              }
              inputProps={{ min: 1 }}
            />
          ) : null}
          <TextField
            label="Note (optional)"
            size="small"
            multiline
            minRows={2}
            value={grantForm.note}
            onChange={(e) => setGrantForm({ ...grantForm, note: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGrantOpen(false)} disabled={granting}>
            Cancel
          </Button>
          <Button variant="contained" onClick={submitGrant} disabled={granting}>
            {granting ? "Granting…" : "Grant bonus time"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
